import { renderRichTextToComponent } from "@components/cms/rich-text-renderer/renderRichTextToComponent";
import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import Modal from "@components/ui/modals/Modal";
import ModalContent from "@components/ui/modals/ModalContent";
import { Heading } from "@ui/components/content/heading/Heading";
import type { ModalProps } from "@ui/components/overlay/modal/Modal";
import { ModalBody, ModalOverlay } from "@ui/components/overlay/modal/Modal";
import type { StyleProps } from "@ui/types";
import type { Document } from "@contentful/rich-text-types";

import type { PropsWithChildren } from "react";
import { useCallback } from "react";

type CustomStyle = {
    content: StyleProps;
};

type Props = {
    description?: string;
    label: string;
    customStyle?: CustomStyle;
    returnFocusOnClose?: ModalProps["returnFocusOnClose"];
    isOpen?: ModalProps["isOpen"];
    descriptionRichText?: Document;
    onClose?: () => void;
} & PropsWithChildren;

export default function BaseModal({
    description,
    label,
    children,
    customStyle,
    descriptionRichText,
    isOpen,
    onClose,
    returnFocusOnClose,
}: Props) {
    const { hideModal } = useGlobalModalContext();

    const closeHandler = useCallback(() => {
        hideModal();
    }, [hideModal]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose ?? closeHandler}
            closeOnOverlayClick={true}
            returnFocusOnClose={returnFocusOnClose}
        >
            <ModalOverlay />
            <ModalContent
                py={10.4}
                px={10.4}
                pt={6}
                pl={6}
                maxW={"104"}
                gap={0}
                {...(customStyle?.content ?? {})}
            >
                <Heading mb={6} size="h5">
                    {label}
                </Heading>
                <ModalBody>
                    {description}
                    {children}
                    {descriptionRichText && renderRichTextToComponent(descriptionRichText)}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
